import * as _ from 'lodash'

// import { Cursor } from './lib/Cursor'
// import { Step } from './types/document'
import { newId } from './lib/id'
import { Cursor, CursorV } from 'src/lib/Cursor';
import * as Model from 'src/types/document';
import { documentC, network, docC, docAtom } from 'src/globals';
import { arrInsert } from 'src/util';
import { parentSteps } from 'src/model/steps';
import { nodeInfo, GraphNodeInfo, NodeInfo, referredOrOwnNodeId } from 'src/model/nodeInfo';
import { definitionById } from 'src/model/document';
import Graph from 'src/view/Graph';


// This function is to be wrapped around every interaction callback. It is not
// used here, to ensure that any composite actions which pass through invalid
// intermediate steps blah blah
export function act<T>(func: () => T): T {
  return docAtom.batch('render', () => network.sample(() =>
    func()
  ))
}


// export function setOutputOfDefinitionToCell({definition, cell, ...rest}) {
//   assertAllPresent({definition, cell}, rest)
//
//   definition.outputs = [ { cell: cell.id } ]
// }


export function makeNewBlock(): Model.Block {
  return {
    id: newId('block'),
    inputs: [
    ],
    steps: [
    ]
  }
}

export function makeNewDefinition(): Model.Definition {
  return {
    id: newId('definition'),
    name: 'new function',
    inputs: [
    ],
    steps: [
    ]
  }
}

export function makeNewGraph(): Model.Graph {
  return {
    id: newId('graph'),
    stepType: 'graph',
    nodes: {
    },
    calls: {
    }
  }
}

export function makeNewText(): Model.Text {
  return {
    id: newId('text'),
    stepType: 'text',
    source: 'new text'
  }
}

export function makeNewSwitch(): Model.Switch {
  const switchId = newId('switch')
  return {
    id: switchId,
    stepType: 'switch',
    cases: [
      {
        condition: [],
        steps: []
      },
      {
        condition: undefined,
        steps: []
      }
    ],
    output: {
      id: newId('node'),
      name: 'switch output'
    }
  }
}


export function addNodeToGraph(graphC: Cursor<Model.Graph>, name?: string): Model.NodeId {
  const nodeId = newId('node')
  const node: Model.Node = {
    id: nodeId,
    name: name || ""
  }
  graphC.select('nodes').update((nodes) => ({...nodes, [nodeId]: node}))
  return nodeId
}

// export function addCellToDefinition({definition, ...rest}) {
//   assertAllPresent({definition}, rest)
//   const cellId = newId('cell')
//   const cell = { id: cellId }
//   definition.cells[cellId] = cell
//   return cell
// }

export function insertStepIntoSteps<T extends Model.Step>(step: T, stepsC: Cursor<Model.Steps>, index: number) {
  stepsC.update((steps) => arrInsert(steps, index, step))
  return stepsC.select(index) as any as Cursor<T>  // we know what it is; we just inserted it
}

export function addReferenceNodeToGraph(nodeId: string, graphC: Cursor<Model.Graph>, blockC: Cursor<Model.Block>): Model.NodeId {
  const info = nodeInfo(blockC, nodeId)
  if (!info.name) {
    NodeInfo.nodeC(info)._('name').set('nameless node')
  }
  return addCodeCallFromRaw(nodeId, graphC, true).nodeId
}

export function insertNewGraphIntoStepsWithReference(nodeId: Model.NodeId, stepsC: Cursor<Model.Steps>, index: number, blockC: Cursor<Model.Block>) {
  const newGraphC = insertStepIntoSteps(makeNewGraph(), stepsC, index)
  const newNodeId = addReferenceNodeToGraph(nodeId, newGraphC, blockC)
  return {graphC: newGraphC, nodeId: newNodeId}
}

export function deleteStepFromSteps(stepC: Cursor<Model.Step>) {
  const stepsC = parentSteps(stepC)
  stepsC.update((steps) => _.without(steps, stepC.get()))
}

// OK if you right-click a node (X) and say "make a code call from that!" then what happens?
// * If X is in a graph, and X doesn't have a call coming from it, you just tack your call onto the end of that puppy
// * If not, you're going to have to make a new graph, put a reference to X in there, and then make a code call from the reference
//     (if the node is in a step (graph or switch output) we make the graph right after that step;
//      if not (function input) we make the graph at the end of the body?)
export function prepareNodeForNewCall(blockC: Cursor<Model.Block>, nodeId: Model.NodeId) {
  const info = nodeInfo(blockC, nodeId)
  if (info.type === 'graph' && !info.usingCallId) {
    return {nodeId, graphC: NodeInfo.graphC(info)}
  } else {
    let stepsC: Cursor<Model.Steps>
    let index: number
    switch (info.type) {
    case 'input': {
      stepsC = blockC._('steps')
      index = stepsC.get().length
      break }
    case 'graph': {
      const stepC = NodeInfo.graphC(info)
      stepsC = parentSteps(stepC)
      index = stepsC.get().indexOf(stepC.get()) + 1
      break }
    case 'switch-output': {
      const stepC = NodeInfo.switchC(info)
      stepsC = parentSteps(stepC)
      index = stepsC.get().indexOf(stepC.get()) + 1
      break }
    }
    const {nodeId: newRefNodeId, graphC: newGraphC} =
      insertNewGraphIntoStepsWithReference(referredOrOwnNodeId(blockC, nodeId), stepsC!, index!, blockC)
    // console.log("inserted new graph!", newRefNodeId, newGraphC)
    return {nodeId: newRefNodeId, graphC: newGraphC}
  }
}

export function addCodeCallFrom(blockC: Cursor<Model.Block>, nodeId: Model.NodeId, codeMaker = (inputNodeId: string) => inputNodeId) {
  const {nodeId: newNodeId, graphC} = prepareNodeForNewCall(blockC, nodeId)
  return addCodeCallFromRaw(newNodeId, graphC, false, codeMaker)
}

// This function adds a code call from a node, into a given graph. The node
// given does not have to be in the graph, but if it is indeed not the graph,
// the call better be collapsed! (so you're creating a reference.)
export function addCodeCallFromRaw(inputNodeId: Model.NodeId, graphC: Cursor<Model.Graph>, collapsed: boolean = false, codeMaker = (inputNodeId: string) => inputNodeId) {
  const newOutputNodeId = addNodeToGraph(graphC)
  const newCallId = newId('call')
  const newCall: Model.CodeCall = {
    id: newCallId,
    callType: 'code',
    inputs: [
      {
        outside: inputNodeId,
        inside: inputNodeId
      }
    ],
    code: codeMaker(inputNodeId),
    outputs: [
      {
        outside: newOutputNodeId
      }
    ],
    collapsed: collapsed
  }
  graphC.select('calls').update((calls) => ({...calls, [newCallId]: newCall}))
  return { callId: newCallId, nodeId: newOutputNodeId }
}

export function addDefinitionCallFrom(blockC: Cursor<Model.Block>, nodeId: Model.NodeId, definitionId: Model.DefinitionId) {
  const {nodeId: newNodeId, graphC} = prepareNodeForNewCall(blockC, nodeId)
  addDefinitionCallFromRaw(newNodeId, graphC, definitionId)
}

// This function adds a code call from a node, into a given graph. The node
// given does not have to be in the graph, but if it is indeed not the graph,
// the call better be collapsed! (so you're creating a reference.)
export function addDefinitionCallFromRaw(inputNodeId: Model.NodeId, graphC: Cursor<Model.Graph>, definitionId: Model.DefinitionId) {
  const newCall: Model.DefinitionCall = {
    id: newId('call'),
    callType: 'definition',
    inputs:
      definitionById(definitionId).get().inputs.map(({id}, i) => ({
        outside: i === 0 ? inputNodeId : addEmptyCodeCall(graphC),
        inside: id
      })),
    definition: definitionId,
    outputs: [
      {
        outside: addNodeToGraph(graphC)
      }
    ]
  }
  graphC.select('calls').update((calls) => ({...calls, [newCall.id]: newCall}))
  return newCall.outputs[0].outside
}

export function addDefinitionCallWithDefaults(graphC: Cursor<Model.Graph>, definitionId: Model.DefinitionId) {
  const newCall: Model.DefinitionCall = {
    id: newId('call'),
    callType: 'definition',
    inputs:
      definitionById(definitionId).get().inputs.map((input, i) => ({
        outside: addEmptyCodeCall(graphC, undefined, input.settings[input.activeSetting]),
        inside: input.id
      })),
    definition: definitionId,
    outputs: [
      {
        outside: addNodeToGraph(graphC)
      }
    ]
  }
  graphC.select('calls').update((calls) => ({...calls, [newCall.id]: newCall}))
  return newCall.outputs[0].outside
}

export function addListOpCallFrom(blockC: Cursor<Model.Block>, nodeId: Model.NodeId, listOpType: Model.ListOpType) {
  const {nodeId: newNodeId, graphC} = prepareNodeForNewCall(blockC, nodeId)
  addListOpCallFromRaw(newNodeId, graphC, listOpType)
}

export function addListOpCallFromRaw(inputNodeId: Model.NodeId, graphC: Cursor<Model.Graph>, listOpType: Model.ListOpType) {
  const newOutputNodeId = addNodeToGraph(graphC)
  const newCallId = newId('call')
  const elementNodeId = newId('node')
  const indexNodeId = newId('node')
  const identityGraphId = newId('graph')
  const identityNodeId = newId('node')
  const identityCallId = newId('call')
  const newCall: Model.ListOpCall = {
    id: newCallId,
    callType: 'list-op',
    inputs: [
      {
        outside: inputNodeId,
        inside: inputNodeId
      }
    ],
    listOpType,
    block: {
      id: newId('block'),
      inputs: [
        { id: elementNodeId, name: 'item' },
        { id: indexNodeId,   name: 'index' },
      ],
      steps: [{
        stepType: 'graph',
        id: identityGraphId,
        nodes: {
          [identityNodeId]: {
            id: identityNodeId,
            name: ""
          }
        },
        calls: {
          [identityCallId]: {
            id: identityCallId,
            callType: 'code',
            inputs: [{
              outside: elementNodeId,
              inside: elementNodeId,
            }],
            code: elementNodeId,
            outputs: [{
              outside: identityNodeId
            }],
            collapsed: true
          }
        }
      }]
    },
    elementNodeId,
    indexNodeId,
    outputs: [
      {
        outside: newOutputNodeId
      }
    ]
  }
  graphC.select('calls').update((calls) => ({...calls, [newCallId]: newCall}))
  return newOutputNodeId
}

export function addCombiningCodeCallRaw(
    nodeId1: Model.NodeId, nodeId2: Model.NodeId,
    graphC: Cursor<Model.Graph>, blockC: Cursor<Model.Block>) {
  const newCallId = addCodeCallFromRaw(nodeId1, graphC).callId
  const newCallC = graphC._('calls')._(newCallId) as Cursor<Model.CodeCall>
  // console.log("newCallC", newCallId newCallC, newCallC.get())
  addInput(newCallC, nodeId2, 1)
  newCallC._('code').set(`[${nodeId1}, ${nodeId2}]`)
}

export function addEmptyCodeCall(graphC: Cursor<Model.Graph>, name?: string, code?: string) {
  const newOutputNodeId = addNodeToGraph(graphC, name)
  const newCallId = newId('call')
  const newCall: Model.CodeCall = {
    id: newCallId,
    callType: 'code',
    inputs: [
    ],
    code: code || "",
    outputs: [
      {
        outside: newOutputNodeId
      }
    ],
    collapsed: true
  }
  graphC.select('calls').update((calls) => ({...calls, [newCallId]: newCall}))
  return newOutputNodeId
}
//
// export function deleteCallFromGraph({call, graph, ...rest}) {
//   assertAllPresent({call, graph}, rest)
//
//   throw new Error("Not implemented")
// }

export function deleteInputFromCall(inputNodeId: Model.NodeId, callId: Model.CallId, graphC: Cursor<Model.Graph>, blockC: Cursor<Model.Block>) {
  console.log("deleteInputFromCall", arguments)
  deleteSubtree(blockC, inputNodeId)
  graphC._('calls')._(callId)._('inputs').update((inputs) =>
    _.filter(inputs, (input) => input.outside !== inputNodeId))
}

export function replaceInput(callC: Cursor<Model.Call>, oldNodeId: Model.NodeId, newNodeId: Model.NodeId) {
  const inputIndex = _.findIndex(callC.get().inputs, {outside: oldNodeId})
  callC._('inputs')._(inputIndex)._('outside').set(newNodeId)
}

export function addInput(callC: Cursor<Model.Call>, nodeId: Model.NodeId, index?: number) {
  callC._('inputs').update((inputs) => arrInsert(inputs, index || callC.get().inputs.length, {
    outside: nodeId, inside: nodeId
  }))
}

export function moveSubtree(
    blockC: Cursor<Model.Block>,
    nodeId: Model.NodeId,
    newGraphC: Cursor<Model.Graph>
    ) {
  console.log("movesubtree")
  const oldGraphC = NodeInfo.graphC(nodeInfo(blockC, nodeId) as GraphNodeInfo)
  console.log('oldGraphC', oldGraphC, nodeInfo(blockC, nodeId))
  moveSubtreeIntoGraphLeavingHoleIfNeeded(blockC, nodeId, oldGraphC, newGraphC)
  deleteGraphIfEmpty(oldGraphC)
}

export function moveSubtreeIntoGraphLeavingHoleIfNeeded(
    blockC: Cursor<Model.Block>,
    nodeId: Model.NodeId,
    oldGraphC: Cursor<Model.Graph>,
    newGraphC: Cursor<Model.Graph>) {
  // Replace the node with an empty node, if needed
  const usingCallId = (nodeInfo(blockC, nodeId) as GraphNodeInfo).usingCallId
  if (usingCallId) {
    const emptyNodeId = addEmptyCodeCall(oldGraphC)
    replaceInput(oldGraphC.select('calls').select(usingCallId), nodeId, emptyNodeId)
  }

  // And do the move!
  moveSubtreeIntoGraph(blockC, oldGraphC, newGraphC, nodeId)
}

// export function moveSubtreeIntoGraphReplacingNode({node, graph, newGraph, nodeToReplace, definition, ...rest}) {
//   assertAllPresent({node, graph, newGraph, nodeToReplace, definition}, rest)
//
//   moveSubtreeIntoGraphLeavingHoleIfNeeded({node, graph, newGraph, definition})
//   deleteSubtree({node: nodeToReplace, graph: newGraph})
//
//   const outputCall = callUsingNode(nodeToReplace, newGraph)
//   if (outputCall) {
//     replaceInput({call: outputCall, oldNode: nodeToReplace, newNode: node})
//   }
// }
//
// export function addReferenceNodeToGraphReplacingNode({cell, graph, nodeToReplace, definition, ...rest}) {
//   assertAllPresent({cell, graph, nodeToReplace, definition}, rest)
//
//   const referenceNode = addReferenceNodeToGraph({cell, graph, definition})
//   deleteSubtree({node: nodeToReplace, graph})
//
//   const outputCall = callUsingNode(nodeToReplace, graph)
//   if (outputCall) {
//     replaceInput({call: outputCall, oldNode: nodeToReplace, newNode: referenceNode})
//   }
// }
//

export function collapseCall(callC: Cursor<Model.CodeCall>, graphC: Cursor<Model.Graph>, blockC: Cursor<Model.Block>) {
  // We assume all inputs are references! Check this yourself!
  CursorV.elems(callC._('inputs').fetch()).forEach((inputC) => {
    const oldInputNodeId = inputC.value().outside
    const referredNodeId = (nodeInfo(blockC, oldInputNodeId) as GraphNodeInfo).referredNodeId!
    deleteSubtree(blockC, oldInputNodeId)
    inputC._('outside').set(referredNodeId)
  })
  callC._('collapsed').set(true)
}

export function expandCall(callC: Cursor<Model.CodeCall>, graphC: Cursor<Model.Graph>, blockC: Cursor<Model.Block>) {
  CursorV.elems(callC._('inputs').fetch()).forEach((inputC) => {
    const oldInputNodeId = inputC.value().outside
    const newReference = addReferenceNodeToGraph(oldInputNodeId, graphC, blockC)
    inputC._('outside').set(newReference)
  })
  callC._('collapsed').set(false)
}

export function moveSubtreeIntoGraph(blockC: Cursor<Model.Block>, oldGraphC: Cursor<Model.Graph>, newGraphC: Cursor<Model.Graph>, nodeId: Model.NodeId) {
  // If it has a defining call...
  const definingCallId = (nodeInfo(blockC, nodeId) as GraphNodeInfo).definingCallId
  if (definingCallId) {
    const definingCallC = oldGraphC._('calls')._(definingCallId)
    
    // ...move the call's inputs
    definingCallC.get().inputs.forEach(({outside}) => {
      if (oldGraphC.get().nodes[outside]) {
        moveSubtreeIntoGraph(blockC, oldGraphC, newGraphC, outside)
      }
    })

    // ...and the call itself
    newGraphC._('calls').update((calls) => ({...calls, [definingCallId]: definingCallC.get()}))
    definingCallC.delete()
  }

  // Move the node itself
  console.log('graphC is', oldGraphC)
  const oldNodeC = oldGraphC._('nodes')._(nodeId)
  newGraphC._('nodes').update((nodes) => ({...nodes, [nodeId]: oldNodeC.get()}))
  oldNodeC.delete()
}

export function deleteGraphIfEmpty(graphC: Cursor<Model.Graph>) {
  console.log('deleteGraphIfEmpty', graphC.get().id, graphC.get().nodes)
  if (_.isEmpty(graphC.get().nodes)) {
    console.log("it's empty!")
    deleteStepFromSteps(graphC)
  }
}

export function deleteSubtree(blockC: Cursor<Model.Block>, nodeId: Model.NodeId) {
  console.log('deleteSubtree', nodeId)
  const info = nodeInfo(blockC, nodeId) as GraphNodeInfo
  const graphC = NodeInfo.graphC(info)

  // Delete the node itself
  graphC.select('nodes').update((nodes) => _.omit(nodes, [nodeId]))

  // If it has a defining call...
  const definingCallId = info.definingCallId
  if (definingCallId) {
    const definingCall = graphC.get().calls[definingCallId]
    // and it's not collapsed...
    if (!(definingCall.callType === 'code' && definingCall.collapsed)) {
      // ...delete the call's inputs
      graphC.get().calls[definingCallId].inputs.forEach(({outside}) =>
        deleteSubtree(blockC, outside)
      )
    }

    // ...and the call itself
    graphC.select('calls').update((calls) => _.omit(calls, [definingCallId]))
  }

  // // If it is used in a call...
  // const usingCall = callUsingNode(node, graph)
  // if (usingCall && usingCall.type === 'call-definition') {
  //
  // }
}

export function ensureCallHasInputForNode(blockC: Cursor<Model.Block>, callC: Cursor<Model.CodeCall>, nodeId: Model.NodeId) {
  if (callC._('collapsed').get()) {
    const existingInputForCell = _.find(callC.get().inputs, ({outside}) => {
      return outside === nodeId
    })
    if (existingInputForCell) {
      return existingInputForCell.inside
    } else {
      addInput(callC, nodeId)
      return nodeId
    }
  } else {
    const existingInputForCell = _.find(callC.get().inputs, ({outside}) => {
      return (nodeInfo(blockC, outside) as GraphNodeInfo).referredNodeId === nodeId
    })
    if (existingInputForCell) {
      return existingInputForCell.inside
    } else {
      const referenceNodeId = addReferenceNodeToGraph(nodeId, callC.parent().parent(), blockC)
      addInput(callC, referenceNodeId)
      return referenceNodeId
    }
  }
}

// TODO: this hackishly uses the fact that IDs are not actually global, but just per-definition
// E.g., blocks in list operations won't actually have unique IDs! nooooooo
export function duplicateDefinition(definitionC: Cursor<Model.Definition>) {
  const definition = definitionC.get()
  const newDefinition = {...definition, id: newId('definition'), name: 'copy of ' + definition.name}
  documentC.select('definitions').update((definitions) =>
    arrInsert(definitions, definitions.indexOf(definition) + 1, newDefinition)
  )
}

export function deleteDefinition(definitionC: Cursor<Model.Definition>) {
  documentC.select('definitions').update((definitions) => _.without(definitions, definitionC.get()))
}

// export function toggleIsMuted({blockC, ...rest}) {
//   assertAllPresent({blockC}, rest)
//
//   docTree.apply(blockC.select('isMuted'), (isMuted) => !isMuted)
// }
//
// export function toggleIsVisualization({blockC, ...rest}) {
//   assertAllPresent({blockC}, rest)
//
//   docTree.apply(blockC.select('isVisualization'), (isVisualization) => !isVisualization)
// }



// WEBPACK FOOTER //
// ./src/actions.ts